import * as React from "react";
import styled from "styled-components";

import { topNavHeightPx } from "../../../View/Products/Constants/StyleConstants";

interface INavigationDropdownProps {
    dropdownPosition: string;
    triggerWidth?: number;
    triggerType?: string;
    isMainNavItem?: boolean;
}

interface INavItemProps {
    padding?: string;
    custom?: string;
    type?: string;
    isMainNavItem?: boolean;
}

interface INavDropdownProps {
    dropdownPosition?: string;
    triggerWidth?: number;
    className?: string;
    trigger: string | JSX.Element;
    triggerType: string;
    triggerClass?: string;
    triggerPadding?: string;
    triggerCustom?: string;
    children: any;
    isMainNavItem?: boolean;
}

interface INavDropdownState {
    open: boolean;
}

const NavDropdownItemStyle = () => {
    return `color: #3d414a;
    background-color: transparent;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: normal;
    font-size: 14px;
    transition: background-color 0.3s, padding 0.3s;
    > * {
        color: inherit;
    }
    &:first-child {
        border-radius: 4px 4px 0 0;
    }
    &:last-child {
        border-radius: 0 0 4px 4px;
    }
`;
};

const centerDropdown = `
    right: 50%;
    transform: translateX(50%);
`;

const rightDropdown = (triggerWidth: number) => `
    right: calc(100% - ${triggerWidth}px);
`;

const leftInnerDropdown = `
    right: initial;
    left: -3px;
    transform: translateX(-100%);
    top: 0;
`;

const NavItemBaseStyles = (props: INavItemProps) => {
    return `box-sizing: border-box;
    height: 56px;
    text-decoration: none;
    color: #3d414a;
    display: flex;
    justify-content: center;
    align-items: center;
    letter-spacing: 2px;
    border-bottom: 1.75px solid transparent;
    text-transform: uppercase;
    transition: color 0.3s, border-color 0.3s;
    &.border-right {
        border-right: 1px solid rgba(67, 74, 84, 0.11);
    }
    &.full-screen {
        display: flex;
    }
    ${props.padding ? `${props.padding}` : `padding: 19px 2% 20px;`}
    ${props.custom ? `${props.custom}` : ``}
    @media screen and (min-width: 1025px) {
        ${props.type !== "NavDropdownItem" && props.padding
        ? `${props.padding}`
        : `padding: 19px 19px 20px;`
}
    }
`;
};

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  position: fixed;
  top: ${topNavHeightPx}px;
  z-index: 3;
  height: ${topNavHeightPx}px;
  width: 100vw;
  padding: 0 16px 0 58px;
  font-family: Manrope, sans-serif;
  font-size: 10px;
  color: #3d414a;
  background-color: white;
  font-weight: 900;
  box-shadow: 0 2px 3px 0 rgba(67, 74, 84, 0.11);
  opacity: 0;
  transition: opacity 0.4s;
  &.visible {
    opacity: 1;
  }
  + section {
    height: 100%;
  }
`;

const NavSection = styled.div`
  display: flex;
  flex-grow: 0;
  align-items: center;
  &:nth-of-type(2) {
    justify-content: flex-end;
    > *,
    > *:hover {
      border-bottom: 1px solid transparent;
    }
  }
  > img {
    margin-right: 2vw;
    @media screen and (min-width: 1024px) {
      margin-right: 5vw;
    }
  }
`;

const NavigationDropdown = styled.div`
  position: relative;
  transition: padding 0.3s;
  height: ${(props) =>
        props.isMainNavItem
            ? `${topNavHeightPx}px;`
            : "34px;"}

  .dropdown-list {
    position: absolute;
    background-color: white;
    border-radius: 4px;
    margin-top: 2px;
    min-width: 200px;
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.15);
    visibility: hidden;
    transition: opacity 0.3s, visibility 0s linear 0.3s;
    opacity: 0;
    z-index: 4;

    ${(props: INavigationDropdownProps) =>
            props.dropdownPosition === "right"
                    ? `${rightDropdown(props.triggerWidth)}`
                    : `${centerDropdown}`}
    ${(props: INavigationDropdownProps) =>
            props.dropdownPosition === "left"
            && props.triggerType === "NavDropdownItem"
                    ? `${leftInnerDropdown}`
                    : ``}
    > * {
        padding: 2px 16px;
    }
}
    
  &.open {
    > .dropdown-list {
      opacity: 1;
      visibility: visible;
      transition: opacity 0.3s, visibility 0s;
      > * {
        &:hover {
          background-color: rgba(67, 74, 84, 0.1);
        }
      }
    }
  }
`;

const NavItem = styled("span") <INavItemProps>`
  cursor: pointer;
  white-space: nowrap;
  height: 34px;

  a:focus {
    text-decoration: none;
  }

  &:hover,
  &.active,
  ${NavigationDropdown}:hover & {
    border-color: #e6c804;
  }
  ${(props) =>
        props.type === "NavDropdownItem"
            ? NavDropdownItemStyle
            : NavItemBaseStyles(props)}
  input, button {
    border: 0;
    background-color: transparent;
    padding: 0;
  }
  form,
  form input,
  form button {
    cursor: pointer;
  }
`;

const Counter = styled.span`
  display: inline-block;
  background-color: red;
  color: white;
  border-radius: 12px;
  box-sizing: border-box;
  line-height: 11px;
  min-width: 20px;
  height: 20px;
  padding: 4px 6px 4px 7px;
  margin-left: 4px;
`;

/**
 * Nav dropdown type
 * @param props (obj)
 */
class NavDropdown extends React.Component<
    INavDropdownProps,
    INavDropdownState
> {
    private dropdownList = React.createRef<HTMLDivElement>();

    constructor(props: INavDropdownProps) {
        super(props);

        this.state = {
            open: false,
        };
    }

    public showMenu = (event) => {
        event.preventDefault();

        this.setState({ open: true }, () => {
            document.addEventListener("click", this.closeMenu);
        });
    };

    public closeMenu = (event) => {
        if (
            !this.dropdownList.current.contains(event.target)
            || event.target.children.length === 0
        ) {
            this.setState({ open: false }, () => {
                document.removeEventListener("click", this.closeMenu);
            });
        }
    };

    public render() {
        return (
            <NavigationDropdown
                dropdownPosition={this.props.dropdownPosition}
                triggerWidth={this.props.triggerWidth}
                className={`${this.props.className ? this.props.className : " "} ${this.state.open ? "open" : " "}`}
                triggerType={this.props.triggerType}
                onClick={this.showMenu}
                isMainNavItem={this.props.isMainNavItem}
            >
                <NavItem
                    className={this.props.triggerClass}
                    padding={this.props.triggerPadding}
                    custom={this.props.triggerCustom}
                    type={this.props.triggerType}
                    isMainNavItem={this.props.isMainNavItem}
                >
                    {this.props.trigger}
                </NavItem>
                <div className="dropdown-list" ref={this.dropdownList}>
                    {this.props.children}
                </div>
            </NavigationDropdown>
        );
    }
}

export { Nav, NavSection, NavItem, NavDropdown, Counter };
